/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-template-curly-in-string */
import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import memoize from 'lru-memoize';

import { logout } from '../auth/redux/authActions';
import { keycloak } from '../../keycloak-config';
import { setLoading } from '../../redux/loading/loadingActions';
import { ErrorStatus } from '../../api/portal/portalApi';
import { profileActions, profileData } from '../../redux/reducers/profile';
import { createValidator, required, phone } from '../../redux/utils/validation';
import { useAppSelector, useAppDispatch } from '../../hooks/useRedux';

import { FormInput } from '../../elements/Form';
import { showError, showSuccess } from '../../elements/Toasts/toastActions';
import Button from '../../elements/Button/Button';

import SessionOutdated from '../../pages/common/SessionOutdated';
import { ReactComponent as SaveIcon } from '../../styles/icon/save.svg';
import strings from '../../lang/lang';
import styles from './Profile.module.scss';

type formDataProps = {
  firstName: string,
  lastName: string,
  phone: string,
  company: string,
}

const getRequest = (props) => {
  const { firstName, lastName, phone, company } = props;
  return {
    firstName: firstName ? firstName.trim() : firstName,
    lastName: lastName ? lastName.trim() : lastName,
    phone: phone ? phone.trim() : phone,
    company: company ? company.trim() : company,
  };
};

const Profile: React.FC = () => {
  const [sessionOutdatedDialog, setSessionOutdatedDialog] = useState(false);
  const dispatch = useAppDispatch();
  const profile = useAppSelector(profileData);

  useEffect(() => {
    loadProfileData();
  }, []);

  function loadProfileData() {
    return new Promise((res, rej) => {
      setLoading(true);
      dispatch(profileActions.loadProfile({ res, rej }));
    })
    .catch(({ status }) => {
      if (status === 401) {
        logout();
      }
      showError(strings.profile.errors.retrievingUserProfile);
    })
    .finally(() => {
      setLoading(false);
    });
  }

  function onSubmit(formData: formDataProps) {
    return new Promise((res, rej) => {
      setLoading(true);
      const data = getRequest(formData);
      dispatch(profileActions.updateProfile({ data, res, rej }));
    })
    .then(() => {
      showSuccess(strings.profile.text.savedProfile);
    })
    .catch(({ status, data }) => {
      if (status === 401) {
        logout();
      } else if (data.name && ErrorStatus.SESSION_OUTDATED === data.name) {
        setSessionOutdatedDialog(true);
      } else {
        showError(strings.profile.errors.savingException);
      }
    })
    .finally(() => {
      setLoading(false);
    });
  }

  const validate = memoize(10)(
    createValidator({
      firstName: [required],
      lastName: [required],
      phone: [required, phone],
      company: [required],
    })
  );

  return (
    <div className={styles.profile}>
      <h1 className={styles.profile__title}>Profile</h1>
      <h2>{strings.profile.text.title}</h2>
      <Form
        onSubmit={onSubmit}
        validate={validate}
        initialValues={profile}
        render={({ handleSubmit }) => {
          return (
            <form onSubmit={handleSubmit}>
              <div className={styles.profile__items}>
                <div className={styles.profile__item}>
                  <FormInput
                    className="profile"
                    id="name"
                    name="name"
                    label={strings.profile.fields.userName}
                    disabled
                  />
                </div>
                <div className={styles.profile__item}>
                  <FormInput
                    className="profile"
                    id="email"
                    name="email"
                    label={strings.profile.fields.email}
                    disabled
                  />
                </div>
                <div className={styles.profile__item}>
                  <FormInput
                    className="profile"
                    id="firstName"
                    name="firstName"
                    label={strings.profile.fields.firstName}
                  />
                </div>
                <div className={styles.profile__item}>
                  <FormInput
                    className="profile"
                    id="lastName"
                    name="lastName"
                    label={strings.profile.fields.lastName}
                  />
                </div>
                <div className={styles.profile__item}>
                  <FormInput
                    className="profile"
                    id="phone"
                    name="phone"
                    label={strings.profile.fields.phone}
                  />
                </div>
                <div className={styles.profile__item}>
                  <FormInput
                    className="profile"
                    id="company"
                    name="company"
                    label={strings.profile.fields.company}
                  />
                </div>
              </div>
              <div className={styles.profile__btn}>
                <Button
                  type="submit"
                  name={strings.profile.buttons.save}
                  variant="primary"
                  secondaryClassName="lg"
                >
                  <SaveIcon />
                </Button>
                <div className={styles.profile__pass_btn}>
                  <Button
                    type="button"
                    name={strings.profile.buttons.changePass}
                    variant="primary"
                    secondaryClassName="lg"
                    onClick={() => {
                      keycloak.login({
                        action: 'UPDATE_PASSWORD',
                        prompt: 'login',
                      });
                    }}
                  />
                </div>
                <div className={styles.profile__pass_btn}>
                  <Button
                    type="button"
                    name={strings.profile.buttons.enableTwoFactor}
                    variant="primary"
                    secondaryClassName="lg"
                    onClick={() => {
                      keycloak.login({
                        action: 'CONFIGURE_TOTP',
                      });
                    }}
                  />
                </div>
              </div>
            </form>
          );
        }}
      />
      <SessionOutdated
        visibility={sessionOutdatedDialog}
        hide={() => setSessionOutdatedDialog(false)}
      />
    </div>
  );
};

export default Profile;
